import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MobileLayout, DesktopLayout } from '@/components/templates/layouts'
import MainScreen from '@/screens/MainScreen'
import TradingScreen from '@/screens/TradingScreen'
import DataRoomScreen from '@/screens/DataRoomScreen'
// import PartnerPortalScreen from '@/screens/PartnerPortalScreen'
// import PartnerReferralsScreen from '@/screens/PartnerReferralsScreen'
// import PartnershipsScreen from '@/screens/PartnershipsScreen'
import { LoginScreen } from '@/screens/login'
import { SignupScreen } from '@/screens/signup'
import { AttestationScreen } from '@/screens/attestation'
import { ProfilePreferencesScreen } from '@/screens/profile-preferences'
// ProfileSettingsScreen removed - functionality moved to ProfileScreen
import ProfileScreen from '@/screens/ProfileScreen'
import ProjectDetailScreen from '@/screens/data-room/ProjectDetailScreen'
import { useDeviceType } from '@/hooks/useDeviceType'

// Portfolio Tracker imports
import PortfolioTrackerScreen from '@/screens/portfolio/PortfolioTrackerScreen'
import PortfolioCreateScreen from '@/screens/portfolio/PortfolioCreateScreen'
import PortfolioDetailScreen from '@/screens/portfolio/PortfolioDetailScreen'
import PortfolioAssetsScreen from '@/screens/portfolio/PortfolioAssetsScreen'
import AssetDetailScreen from '@/screens/portfolio/AssetDetailScreen'

// Organization (institutional account) imports
import {
  OrganizationsScreen,
  OrganizationDetailScreen,
  AcceptInviteScreen,
  VerifyDomainScreen,
} from '@/screens/organizations'

import { authService } from '@/services/authService'
import { useTheme } from '@/hooks/useTheme'
import { AccessTierProvider, useAccessTier } from '@/hooks/useAccessTier'
import { VerificationRequiredScreen } from '@/components/gate'
import {
  getPendingInviteRedirect,
  hasAcceptedOrgInvite,
  setPendingInviteToken,
} from '@/utils/pendingInvite'

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache kept in memory (formerly cacheTime)
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    },
  },
})

/**
 * The attestation status now lives in `AccessTierProvider`
 * (`src/hooks/useAccessTier.tsx`), along with the fail-closed default that used
 * to be `FAILED_ATTESTATION_STATUS` here. It moved because three things need
 * the same answer — the route guards, the data hooks, and the blurred surfaces
 * — and because a `refresh()` that every consumer sees is what lets completing
 * phase 1 update the app without a full-document reload.
 */

/**
 * The app's one full-screen loading state.
 *
 * Declared at module scope so the route guards can hold on it without remounting
 * a fresh component subtree on every render.
 */
/**
 * `/logout` — the always-reachable exit.
 *
 * This route sits OUTSIDE both route guards on purpose. The guards decide with
 * auth state, and a stale-but-present token used to read as authenticated —
 * so every path to /login bounced the user away and there was no /logout route
 * at all. A user with an expired session was trapped until they cleared
 * localStorage by hand. Logging out must never depend on the very auth state
 * the user is trying to discard.
 *
 * The storage clear runs synchronously in render (idempotent, and safe: it
 * only touches the authService store), so every `authService.isAuthenticated()`
 * check from this instant answers false. The App-level state flip runs in an
 * effect, because setting a parent's state during a child's render is illegal.
 */
const LogoutRoute = ({ onLoggedOut }: { onLoggedOut: () => void }) => {
  authService.logout()

  useEffect(() => {
    // 🔴 Drop every cached response with the session.
    //
    // `queryClient` lives at module scope, and logging out is a client-side
    // navigate rather than a document reload — so without this the next person
    // to log in on this machine inherits the previous user's cached orderbook
    // stats and project data. The access tier is keyed into those query keys,
    // which stops a *tier* change serving stale data, but two users at the
    // same tier share a key by design. Only clearing the cache separates the
    // sessions.
    //
    // In an EFFECT, not in render like the `logout()` above it: clearing the
    // cache notifies every subscribed `useQuery`, and doing that during
    // another component's render is a state update during render. The storage
    // clear has to stay synchronous (every `isAuthenticated()` from that
    // instant must answer false); the cache clear does not.
    queryClient.clear()
    onLoggedOut()
    // `onLoggedOut` is a fresh closure every App render; re-running this
    // effect just re-sets state to false, which is harmless.
  }, [onLoggedOut])

  return <Navigate to="/login" replace />
}

const FullScreenLoader = ({ message = 'Loading trading portal...' }: { message?: string }) => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-gradient">SecondLane</h2>
      <p className="text-foreground opacity-60">{message}</p>
    </motion.div>
  </div>
)

function App() {
  const { isMobile } = useDeviceType()
  // Initialize theme based on system preference
  useTheme()

  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    let isMounted = true

    // Check authentication status
    const checkAuth = async () => {
      try {
        const isAuth = authService.isAuthenticated()
        if (isAuth) {
          // Validate session
          await authService.validateSession()
          if (isMounted) {
            setIsAuthenticated(true)
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        authService.logout()
        if (isMounted) {
          // The tier provider clears itself when `isAuthenticated` goes false.
          setIsAuthenticated(false)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    checkAuth()

    // Listen for auth changes
    const checkAuthPeriodically = setInterval(() => {
      if (isMounted) {
        const isAuth = authService.isAuthenticated()
        setIsAuthenticated(isAuth)
      }
    }, 1000)

    return () => {
      isMounted = false
      clearInterval(checkAuthPeriodically)
    }
  }, [])

  if (isLoading) {
    return <FullScreenLoader />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AccessTierProvider isAuthenticated={isAuthenticated}>
        <AppRoutes
          isAuthenticated={isAuthenticated}
          isMobile={isMobile}
          onLoggedOut={() => setIsAuthenticated(false)}
        />
      </AccessTierProvider>
    </QueryClientProvider>
  )
}

interface AppRoutesProps {
  isAuthenticated: boolean
  isMobile: boolean
  onLoggedOut: () => void
}

/**
 * The route tree, split out of `App` so it can read the access tier.
 *
 * It stays a single component (rather than lifting `ProtectedRoute` and
 * `AuthRoute` to module scope) because both guards close over the same three
 * values and are only meaningful together. They are still declared inside a
 * render body, so their component identity changes on every render of this
 * component — but this component only re-renders when auth or the tier
 * actually changes, and both of those are supposed to remount the tree.
 */
/**
 * 🔴 `GET /attestations/status` failed, so we do not know what this user is.
 *
 * This screen exists because "we could not ask" and "the answer is no" are not
 * the same, and routing on the second when the first is true is how a user
 * mid-verification gets sent back to the account-type picker, re-enters the
 * funnel, and mints a draft that SUPERSEDES the attestation they are waiting
 * on. Holding here costs a user one click; guessing costs them their
 * submission.
 */
const StatusUnavailableScreen = ({ onRetry }: { onRetry: () => void }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4">
    <div className="max-w-[420px] text-center">
      <h2 className="font-plus-jakarta text-xl font-semibold text-foreground">
        We could not check your account
      </h2>
      <p className="mt-2 font-plus-jakarta text-sm text-foreground opacity-70">
        Something went wrong reading your verification status. Nothing is lost —
        your progress is saved on our side. Try again in a moment.
      </p>
      <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={onRetry}
          className="h-11 bg-[#2d2923] px-5 font-plus-jakarta text-sm font-medium text-white transition-opacity hover:opacity-90 dark:bg-[#fdfdfd] dark:text-[#2d2923]"
        >
          Try again
        </button>
        <a
          href="/logout"
          className="h-11 px-5 font-plus-jakarta text-sm font-medium leading-[44px] text-foreground opacity-70 underline"
        >
          Log out
        </a>
      </div>
    </div>
  </div>
)

const AppRoutes = ({ isAuthenticated, isMobile, onLoggedOut }: AppRoutesProps) => {
  const {
    status: attestationStatus,
    canBrowse,
    canReadFinancials,
    resolutionFailed,
    refresh: refreshAccessTier,
  } = useAccessTier()

  // Both `null` (unresolved) exactly when `attestationStatus` is `null`.
  const hasAttestation = attestationStatus === null ? null : attestationStatus.has_attestation
  const pendingAttestation =
    attestationStatus === null ? null : attestationStatus.pending_attestation

  /**
   * Where an *un-onboarded* user resumes the signup funnel.
   *
   * This is a signup-flow breadcrumb, not an authorization signal: it lives in
   * localStorage, so it is absent on a new device, in a fresh profile, after a
   * storage clear, and in incognito - and `AttestationScreen` deletes it as soon
   * as onboarding completes. It may therefore only be consulted for users the
   * server has already told us are NOT onboarded (see `hasAttestation` below).
   *
   * Someone who joined an existing organization by invitation never picks an
   * account type - their organization already exists - so the invite flag
   * satisfies the gate too. Both route guards use this one helper so they cannot
   * drift apart again.
   *
   * 🔴 A user with a `pending_attestation` NEVER needs the picker: they already
   * finished the wizard and submitted — the server says so — even though the
   * wizard deleted the localStorage breadcrumb at completion. Without this
   * clause a KYB-pending user "needs account type selection" by the storage
   * test alone, is sent to /signup, and from the picker can re-enter the funnel
   * (or, on a fresh device, be shown the INDIVIDUAL form) and mint a second
   * draft that supersedes their submitted row. The clause lives here, in the
   * one shared helper, so `ProtectedRoute` and `AuthRoute` cannot disagree
   * about it.
   */
  const needsAccountTypeSelection = () =>
    pendingAttestation === null &&
    !localStorage.getItem('signup_account_type') &&
    !hasAcceptedOrgInvite()

  // Protected Route Component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }

    // Onboarding status not resolved yet. Hold on the loader instead of guessing:
    // redirecting on an unknown status is what used to bounce onboarded users
    // into the signup funnel.
    if (hasAttestation === null) {
      return <FullScreenLoader message="Checking your account..." />
    }

    // 🔴 The status endpoint failed. Every branch below reads the fail-closed
    // fallback, whose `pending_attestation: null` is indistinguishable from
    // "never started" — so a browse-tier user would be routed to /signup and
    // could supersede their own pending attestation. Hold and offer a retry.
    if (resolutionFailed) {
      return <StatusUnavailableScreen onRetry={() => void refreshAccessTier()} />
    }

    // 🔴 THE PHASE-1 DOOR.
    //
    // This used to read `if (hasAttestation)` and send everyone else away.
    // Onboarding is now two phases, and finishing phase 1 — identity plus the
    // entity attestation — is what earns a way in:
    //
    //   `hasAttestation`      COMPLETE attestation. Everything.
    //   `pendingAttestation`  submitted, verification outstanding. Browse only.
    //
    // Letting a pending user in is the whole point of the redesign, so it has
    // to be paired with the thing that makes it safe: `canReadFinancials` is
    // false for that tier, every money surface below tests it, and the routes
    // that are financial end to end are gated a few lines down in
    // `AppContent`. Relaxing this guard WITHOUT those is a data leak.
    if (canBrowse) {
      return <>{children}</>
    }

    // Genuinely mid-onboarding: account type first, then attestation.
    // `needsAccountTypeSelection()` also checks `pendingAttestation`, which is
    // now redundant with the `canBrowse` branch above — kept because
    // `AuthRoute` shares the helper and must not drift from this guard.
    if (needsAccountTypeSelection()) {
      return <Navigate to="/signup" replace />
    }

    // An EXPIRED or SUPERSEDED attestation lands here: neither sets
    // `has_attestation` and neither is reported in `pending_attestation`, so
    // the user is put back at the start of the funnel, which is the correct
    // handling for an attestation that has lapsed or been replaced.
    return <Navigate to="/attestation" replace />
  }

  // Auth Route Component (redirect to home if already authenticated, unless completing signup flow)
  const AuthRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAuthenticated) {
      return <>{children}</>
    }

    // A stashed invitation takes priority: finish accepting it before anything else.
    const pendingInviteRedirect = getPendingInviteRedirect()
    if (pendingInviteRedirect) {
      return <Navigate to={pendingInviteRedirect} replace />
    }

    if (hasAttestation === null) {
      return <FullScreenLoader message="Checking your account..." />
    }

    // Same rule as ProtectedRoute: a failed status read must never let the
    // account-type picker render. See `StatusUnavailableScreen`.
    if (resolutionFailed) {
      return <StatusUnavailableScreen onRetry={() => void refreshAccessTier()} />
    }

    // Only a user the server considers un-onboarded may sit on the account-type
    // step. Mirrors `ProtectedRoute` exactly - this guard previously dropped the
    // invite clause, so an invitee could be held on /signup by one route while
    // the other waved them through.
    //
    // 🔴 The pending_attestation case is covered here through the shared
    // helper: needsAccountTypeSelection() is false while a submitted
    // attestation is waiting on verification, so a KYB-pending user who opens
    // /signup falls through to the Navigate below, re-enters ProtectedRoute,
    // and lands on /attestation — never on the account-type picker. If the two
    // guards ever stop sharing that helper they WILL drift apart again.
    const currentPath = window.location.pathname
    if (!hasAttestation && needsAccountTypeSelection() && currentPath === '/signup') {
      return <>{children}</>
    }

    // Otherwise redirect authenticated users away from auth pages
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AnimatePresence mode="wait">
        <Routes>
          {/* Authentication Routes */}
          <Route 
            path="/login" 
            element={
              <AuthRoute>
                <LoginScreen />
              </AuthRoute>
            } 
          />
          <Route 
            path="/signup" 
            element={
              <AuthRoute>
                <SignupScreen />
              </AuthRoute>
            } 
          />

          {/* Logout - reachable no matter what auth state localStorage holds
              (see LogoutRoute). Before this route existed, /logout fell into
              the `/*` catch-all and ProtectedRoute bounced the user to
              /attestation. */}
          <Route
            path="/logout"
            element={<LogoutRoute onLoggedOut={onLoggedOut} />}
          />

          {/* Organization invitation - deliberately OUTSIDE ProtectedRoute so a
              logged-out invitee keeps their token instead of being bounced away. */}
          <Route
            path="/invite/:token"
            element={<InviteRoute isAuthenticated={isAuthenticated} isMobile={isMobile} />}
          />

          {/* Organization email-domain verification - PUBLIC, and it must stay
              that way. The challenge email goes to an address at the claimed
              domain (an IT/ops contact who typically has no SecondLane account),
              and the backend endpoint is excluded from the auth middleware for
              exactly that reason. Behind ProtectedRoute the whole flow is dead.

              Three literal segments, so React Router ranks this above the `/*`
              catch-all below; and `/organizations/:orgId` is a *two*-segment
              pattern nested inside AppContent, which cannot match this path. */}
          <Route path="/organizations/verify-domain/:token" element={<VerifyDomainScreen />} />

          {/* Attestation Route.
              🔴 Gated on more than authentication. A user who has ALREADY
              finished onboarding has no business here: `pending_attestation`
              is null for them and the wizard deletes `signup_account_type` on
              completion, so `AttestationScreen` would default to the
              INDIVIDUAL form, its debounced autosave would start writing a
              fresh DRAFT within a second, and submitting it would SUPERSEDE
              their COMPLETE attestation — dropping them out of the product
              they had already been let into. Send them home instead.
              A failed status read holds rather than routes, for the reason in
              `StatusUnavailableScreen`. */}
          <Route
            path="/attestation"
            element={
              !isAuthenticated ? (
                <Navigate to="/login" replace />
              ) : resolutionFailed ? (
                <StatusUnavailableScreen onRetry={() => void refreshAccessTier()} />
              ) : hasAttestation ? (
                <Navigate to="/" replace />
              ) : (
                <AttestationScreen />
              )
            }
          />

          {/* Profile Preferences Route */}
          <Route 
            path="/profile-preferences" 
            element={
              isAuthenticated ? <ProfilePreferencesScreen /> : <Navigate to="/login" replace />
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppContent isMobile={isMobile} canReadFinancials={canReadFinancials} />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AnimatePresence>
    </div>
  )
}

/**
 * Public gate for `/invite/:token`.
 *
 * Logged out: persist the token first, then send the invitee to /login (which
 * links to /signup). Both auth screens replay the stashed token afterwards, so
 * the user lands back here and the invitation is accepted.
 * Logged in: accept immediately, exactly as before.
 */
const InviteRoute = ({
  isAuthenticated,
  isMobile,
}: {
  isAuthenticated: boolean
  isMobile: boolean
}) => {
  const { token = '' } = useParams<{ token: string }>()

  if (!isAuthenticated) {
    // Persist synchronously - <Navigate> unmounts us before effects would flush.
    setPendingInviteToken(token)
    return <Navigate to="/login" replace />
  }

  const Layout = isMobile ? MobileLayout : DesktopLayout

  return (
    <Layout>
      <AcceptInviteScreen />
    </Layout>
  )
}

/**
 * App Content Component (for authenticated users).
 *
 * 🔴 Two classes of route, and the difference is not cosmetic.
 *
 * The dashboard is BLURRED for a browse-tier user because a genuine
 * identity-only data source exists for it: `GET /api/v1/orderbook/public`
 * returns the listing with no financial columns at all, and the placeholders
 * are drawn from the absence of those fields.
 *
 * Every route in `FINANCIAL_ROUTES` below is BLOCKED instead, because no such
 * source exists for it. The project data room reads
 * `GET /projects/{id}/graph`, whose chart tooltip enumerates every buy and
 * sell order's FDV and amount as text. The order detail page reads
 * `GET /orderbook/{id}`. The portfolio screens read the user's own positions.
 * There is no version of those pages assembled from data a browse user may
 * hold, and a page built from a hundred individually hidden numbers is a
 * hundred chances to miss one — through a tooltip, a CSV export, an aria-label
 * or a modal. Not fetching is the only treatment that is actually true.
 *
 * A route added here in future is UNGATED by default. If it shows money, add
 * it to the block list.
 */
const AppContent = ({
  isMobile,
  canReadFinancials,
}: {
  isMobile: boolean
  canReadFinancials: boolean
}) => {
  const Layout = isMobile ? MobileLayout : DesktopLayout

  /** Render `screen` for a verified user, the gate for everyone else. */
  const financial = (screen: React.ReactNode, what: string) =>
    canReadFinancials ? screen : <VerificationRequiredScreen what={what} />

  return (
    <Layout>
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<MainScreen />} />
          <Route
            path="/trading/*"
            element={financial(<TradingScreen />, 'The Secondaries market')}
          />
          {/* <Route path="/yield/*" element={<YieldScreen />} /> */}
          {/* <Route path="/rwa/*" element={<RwaScreen />} /> */}
          <Route
            path="/data-rooms/:projectId"
            element={financial(<ProjectDetailScreen />, "This project's data room")}
          />
          <Route
            path="/projects/:projectId"
            element={financial(<ProjectDetailScreen />, "This project's page")}
          />
          <Route
            path="/projects/:projectId/unlock"
            element={financial(<ProjectDetailScreen />, "This project's unlock schedule")}
          />
          <Route
            path="/data-room/*"
            element={financial(<DataRoomScreen />, 'The data room')}
          />
          {/* <Route path="/partnerships/*" element={<PartnershipsScreen />} /> */}
          <Route
            path="/portfolio"
            element={financial(<PortfolioTrackerScreen />, 'Portfolio tracking')}
          />
          <Route
            path="/portfolio/create"
            element={financial(<PortfolioCreateScreen />, 'Portfolio tracking')}
          />
          <Route
            path="/portfolio/:portfolioId"
            element={financial(<PortfolioDetailScreen />, 'Portfolio tracking')}
          />
          <Route
            path="/portfolio/:portfolioId/assets"
            element={financial(<PortfolioAssetsScreen />, 'Portfolio tracking')}
          />
          <Route
            path="/portfolio/:portfolioId/assets/:assetId"
            element={financial(<AssetDetailScreen />, 'Portfolio tracking')}
          />
          {/* <Route path="/partnerships/portal" element={<PartnerPortalScreen />} /> */}
          {/* <Route path="/partnerships/referrals" element={<PartnerReferralsScreen />} /> */}
          {/* Not financial: the user's own account, and the organization
              screens they need in order to FINISH verifying. Blocking those
              would be a deadlock. */}
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          <Route path="/organizations" element={<OrganizationsScreen />} />
          <Route path="/organizations/:orgId" element={<OrganizationDetailScreen />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  )
}

export default App 